import React from 'react'
import { observer } from 'mobx-react'
import { Card, CardContent } from 'components/Card'
import JobViewModel, { Tab } from '../JobViewModel'
import styles from './CommunicationView.sass'
import { useBrandingSettings } from 'hooks/branding'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import ConversationView from 'messaging/components/ConversationView'
import { isNotNil } from 'utils/object-util'
import { ConversationHeaderBanner } from '../../../../../messaging/components/ConversationView/ConversationHeaderBanner'
import { formatToLongDate } from '../../../../../utils/date-util'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: JobViewModel
}

export default observer(function CommunicationView({ vm }: Props) {
  return (
    <div className={styles.root}>
      <Card unconstrained>
        <CardContent scroll compact>
          <ConversationContainer vm={vm} showDocumentTabOnUpload={false} />
        </CardContent>
      </Card>
    </div>
  )
})

interface ConversationContainerProps {
  vm: JobViewModel
  showDocumentTabOnUpload: boolean
}

export const ConversationContainer = observer(
  ({ vm, showDocumentTabOnUpload }: ConversationContainerProps) => {
    const currentMember = vm.jobWorkspaceMember
    const canChat = currentMember.hasAllPermissions('CHAT_VIEW')

    return canChat ? (
      vm.job.conversation === null ? (
        <NoConversation />
      ) : (
        <ConversationView
          vm={vm}
          banner={<OpenTicketConversationBanner vm={vm} />}
          showDocumentTab={() => {
            if (showDocumentTabOnUpload) {
              vm.setTab(Tab.Documents)
            }
          }}
          readOnly={
            vm.job.status === 'CLOSED' ||
            !(
              (currentMember.hasPermission('CHAT_ADD') &&
                vm.job.isCurrentMemberInConversation) ||
              currentMember.hasPermission('ADMIN_CONVERSATION_MANAGE') ||
              vm.job.hasOwnerTeamChatPermission
            )
          }
          welcomeActions={[
            !vm.areNotificationsEnabled && !vm.areNotificationsDenied
              ? {
                  onClick: () => vm.enableNotifications(),
                  children: 'Turn on Notifications',
                }
              : null,
          ].filter(isNotNil)}
        />
      )
    ) : (
      <ChatDisabled />
    )
  },
)

const OpenTicketConversationBanner = observer(
  ({ vm }: { vm: JobViewModel }) => {
    const ticket = vm.jobTicketsVM.latestTicket
    if (!ticket) {
      return null
    }

    const title = (
      <>Open ticket, last update {formatToLongDate(ticket.lastUpdated)}</>
    )

    return (
      <ConversationHeaderBanner title={title}>
        {ticket.statusMessage}
      </ConversationHeaderBanner>
    )
  },
)

export const ChatDisabled = () => {
  const { corporateMode } = useBrandingSettings()
  return (
    <EmptyState
      hideIllustration={corporateMode}
      pad
      title="Chat Disabled"
      animate
      image={
        <EmptyStateIllustrationImage
          src={require('assets/images/img_chat-disabled.svg')}
        />
      }
      subtitle={<>Please contact an administrator to allow chat permissions.</>}
    />
  )
}

export const NoConversation = () => {
  const { corporateMode } = useBrandingSettings()

  return (
    <EmptyState
      hideIllustration={corporateMode}
      pad
      title="No Conversation Yet"
      animate
      image={
        <EmptyStateIllustrationImage
          src={require('assets/images/img_chat-disabled.svg')}
        />
      }
      subtitle={<>Once this job gets submitted, you will be able to chat.</>}
    />
  )
}
